<template>
  <div class="scoresheet">
    <div v-if="rows.length === 0" class="empty">
      <p>Each round's scores will be listed here once it's played.</p>
    </div>
    <div v-else class="table-scroll" :class="{ 'is-crowded': players.length > 4 }">
      <table>
        <caption class="visually-hidden">
          Points per round for each player
        </caption>
        <thead>
          <tr>
            <th scope="col" class="col-round">Round</th>
            <th
              v-for="(player, i) in players"
              :key="player.id"
              scope="col"
              class="col-player"
              :title="player.name"
            >
              <span class="name-full">{{ player.name }}</span>
              <span class="name-short" aria-hidden="true">{{ shortNames[i] }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.number" :class="{ 'is-latest': row.isLatest }">
            <th scope="row" class="col-round">
              <span class="round-cell">
                <span class="round-number">{{ row.number }}</span>
                <span class="index-wrap">
                  <GameIndex :symbol="row.symbol" size="sm" />
                  <span v-if="row.isDouble" class="double-tag" aria-hidden="true">×2</span>
                </span>
                <span class="round-meta">
                  <span class="round-game">
                    {{ row.gameName
                    }}<span v-if="row.isDouble" class="visually-hidden"> (doubled)</span>
                  </span>
                  <span class="round-chooser">
                    <span class="visually-hidden">picked by </span>{{ row.chooser }}
                  </span>
                </span>
              </span>
            </th>
            <td
              v-for="(score, i) in row.scores"
              :key="players[i].id"
              :class="{
                'is-zero': score === 0,
                'is-chooser': players[i].id === row.chooserId
              }"
            >
              {{ formatPoints(score) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" class="col-round">Total</th>
            <td
              v-for="(total, i) in totals"
              :key="players[i].id"
              :class="{ 'is-leader': total === bestTotal }"
            >
              {{ formatTotal(total) }}
            </td>
          </tr>
        </tfoot>
      </table>
      <p class="legend"><span class="legend-dot" aria-hidden="true"></span>Picked the game</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useGameConfigStore } from '../stores/gameConfig';
import { formatPoints, formatTotal } from '../utils/scores';
import GameIndex from './GameIndex.vue';

const props = defineProps({
  players: {
    type: Array,
    required: true
  },
  rounds: {
    type: Array,
    required: true
  }
});

const gameConfig = useGameConfigStore();

const rows = computed(() =>
  props.rounds.map((round, index) => {
    const game = gameConfig.games[round.miniGame.id];
    return {
      number: index + 1,
      symbol: game?.symbol ?? '?',
      gameName: game?.displayName ?? round.miniGame.id,
      isDouble: round.miniGame.isDouble,
      chooserId: round.miniGame.selectedByPlayerId,
      chooser: props.players.find((p) => p.id === round.miniGame.selectedByPlayerId)?.name ?? '',
      isLatest: index === props.rounds.length - 1,
      scores: props.players.map(
        (player) => round.scores.find((s) => s.playerId === player.id)?.score ?? 0
      )
    };
  })
);

const shortNames = computed(() => {
  const names = props.players.map((p) => p.name);
  const isUnique = (name, prefix) =>
    !names.some((other) => other !== name && other.toLowerCase().startsWith(prefix.toLowerCase()));
  return names.map((name) => {
    if (name.length <= 5 || isUnique(name, name.slice(0, 5))) return name.slice(0, 5);
    return name.slice(0, 4) + name.slice(-1);
  });
});

const totals = computed(() =>
  props.players.map((_, i) => rows.value.reduce((sum, row) => sum + row.scores[i], 0))
);

const bestTotal = computed(() => Math.max(...totals.value));
</script>

<style scoped>
.scoresheet {
  --ink: var(--on-table);
  --ink-muted: var(--on-table-muted);
  --card: var(--panel-solid);
  --card-line: rgba(255, 255, 255, 0.06);
  --card-sunk: #1b2233;
  --red: var(--red-on-table);
  --strong: var(--focus);
  container-type: inline-size;
}

.empty {
  padding: 1.5rem 1rem;
  color: var(--on-table-muted);
  text-align: center;
}

.table-scroll {
  overflow-x: auto;
  color: var(--ink);
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--step--1);
}

th,
td {
  padding: 0.5rem 0.625rem;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid var(--card-line);
}

thead th {
  position: sticky;
  top: 0;
  background-color: var(--card);
  font-family: var(--font-display);
  font-size: var(--step-0);
  font-weight: 600;
  padding-top: 0.75rem;
  max-width: 7rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-round {
  text-align: left;
  position: sticky;
  left: 0;
  background-color: var(--card);
  z-index: 1;
}

thead .col-round {
  z-index: 2;
  color: var(--ink-muted);
}

tbody th {
  font-weight: 400;
}

.round-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.round-number {
  width: 1.25rem;
  color: var(--ink-muted);
  text-align: right;
}

.round-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.round-game {
  font-weight: 600;
}

.round-chooser {
  color: var(--ink-muted);
  font-size: 0.8125rem;
}

.index-wrap {
  position: relative;
  display: inline-flex;
}

.double-tag {
  position: absolute;
  top: -6px;
  right: -9px;
  padding: 0 0.2rem;
  border-radius: 4px;
  background-color: var(--gold);
  color: var(--card-ink);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1.35;
}

td {
  font-size: var(--step-0);
  font-weight: 500;
}

td.is-zero {
  color: #6b7487;
}

tbody tr.is-latest th,
tbody tr.is-latest td {
  background-color: var(--card-sunk);
}

tfoot th,
tfoot td {
  border-bottom: none;
  border-top: 2px solid var(--table-line);
  font-family: var(--font-display);
  font-size: var(--step-1);
  font-weight: 700;
  padding-block: 0.625rem 0.75rem;
}

tfoot td.is-leader {
  color: var(--gold);
}

tfoot th {
  font-size: var(--step-0);
}
.name-short,
.legend {
  display: none;
}

.legend {
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem 0.625rem;
  border-top: 1px solid var(--card-line);
  color: var(--ink-muted);
  font-size: 0.8125rem;
}

.legend-dot,
td.is-chooser::before {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--strong);
}

@container (max-width: 520px) {
  table {
    table-layout: fixed;
  }

  th,
  td {
    padding: 0.5rem 0.25rem;
  }

  .col-round {
    width: 3.75rem;
    padding-left: 0.5rem;
  }

  .round-cell {
    gap: 0.375rem;
  }

  .round-number {
    width: 1rem;
    font-size: 0.8125rem;
  }

  .round-meta {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  thead th {
    max-width: none;
    font-size: 0.9375rem;
  }

  .name-full {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .is-crowded .name-full {
    position: absolute;
    width: 1px;
    height: 1px;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .is-crowded .name-short {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td {
    position: relative;
    font-size: 0.9375rem;
  }

  td.is-chooser::before {
    content: '';
    display: inline-block;
    vertical-align: 0.2em;
    margin-right: 0.3125rem;
  }

  tfoot td {
    font-size: var(--step-0);
  }

  .legend {
    display: flex;
  }
}
</style>
